// Write continues the manuscript, so a reader cannot judge continuity, voice
// or tense from the two continuations alone. Each option is shown beneath a
// short, faded excerpt of the text that preceded it — the same text the models
// were given. The excerpt is chosen once per comparison and then frozen, so it
// never shifts the prose while someone is reading.

/** Context shorter than this reads as a fragment, so show nothing instead. */
export const MIN_CONTEXT_WORDS = 24;
/** About a paragraph: enough to hear the voice without competing with the sample. */
export const MAX_CONTEXT_WORDS = 110;
/** How far past the target we may move to land on a paragraph or sentence start. */
const SLACK_WORDS = 30;

export function countWords(text) {
  if (typeof text !== "string" || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

/**
 * Pick the excerpt to show above the options.
 *
 * @param {string} precedingText the manuscript text before the cursor
 * @param {number} wordBudget the shorter option's word count; the excerpt is
 *   never longer than what was generated, so context cannot dominate a short sample
 * @returns {{ paragraphs: string[], wordCount: number, startsMidParagraph: boolean } | null}
 */
export function selectPrecedingSnippet(precedingText, wordBudget) {
  const source = typeof precedingText === "string" ? precedingText.replace(/\r\n?/g, "\n").trim() : "";
  const budget = Math.floor(Number(wordBudget) || 0);
  if (!source || budget < MIN_CONTEXT_WORDS) return null;

  const words = Array.from(source.matchAll(/\S+/g), (match) => ({ start: match.index, end: match.index + match[0].length }));
  if (words.length < MIN_CONTEXT_WORDS) return null;

  const gapBefore = (index) => (index === 0 ? "\n" : source.slice(words[index - 1].end, words[index].start));
  const startsParagraph = (index) => /\n/.test(gapBefore(index));
  const startsSentence = (index) => index === 0 || /[.!?…]["'”’)\]]*$/.test(source.slice(words[index - 1].start, words[index - 1].end));

  const target = Math.max(0, words.length - Math.min(MAX_CONTEXT_WORDS, budget));
  // Never longer than the budget, never shorter than the minimum.
  const earliest = Math.max(0, words.length - budget, target - SLACK_WORDS);
  const latest = Math.min(words.length - MIN_CONTEXT_WORDS, target + SLACK_WORDS);

  let start = target;
  let best = Infinity;
  for (let index = earliest; index <= latest; index += 1) {
    const paragraph = startsParagraph(index);
    if (!paragraph && !startsSentence(index)) continue;
    // A paragraph start is worth a little distance; a sentence start is the fallback.
    const score = Math.abs(index - target) - (paragraph ? 15 : 0);
    if (score < best) {
      best = score;
      start = index;
    }
  }

  const text = source.slice(words[start].start);
  const paragraphs = text.split(/\n+/).map((paragraph) => paragraph.trim()).filter(Boolean);
  return { paragraphs, wordCount: words.length - start, startsMidParagraph: !startsParagraph(start) };
}
